import PageRoutes, { LegacyPageRoutes } from "./constants";

/**
 * Is this path a spotlight surface, and which one?
 *
 * These exist because the alternative did not survive a rename. Nine copies
 * of `location.pathname.includes("my-channel")` and
 * `location.pathname.includes("personal-channel")` were spread across
 * MainContainer and DownloadTabs, and every one of them silently stopped
 * matching the moment the routes moved to /spotlight. Nothing failed loudly:
 * the tab strip simply reappeared on pages that hide it.
 *
 * So the prefixes are derived from the route constants rather than written
 * out, and both predicates also accept the channel-era paths, which are still
 * routed for old bookmarks and for share invitations already sitting in
 * inboxes.
 */

/** "/spotlight/build", the wizard's path with its params stripped. */
const WIZARD_PREFIX = PageRoutes.MyNAHLTV.split("/:")[0];

/** "/spotlight", the saved-spotlight path with its :id stripped. */
const VIEW_PREFIX = PageRoutes.PersonalChannel.split("/:")[0];

/** The build wizard: pick a name, then teams, players and events. */
export function isSpotlightWizardPath(pathname: string): boolean {
  return (
    pathname.startsWith(WIZARD_PREFIX) || pathname.includes("my-channel")
  );
}

/**
 * A saved spotlight.
 *
 * The wizard lives under the view prefix, so it has to be excluded
 * explicitly. Before the rename these two were separate words and could not
 * overlap; now `/spotlight/build/1/2` starts with `/spotlight` and would
 * otherwise answer true to both.
 */
export function isSpotlightViewPath(pathname: string): boolean {
  if (isSpotlightWizardPath(pathname)) return false;
  return (
    pathname.startsWith(`${VIEW_PREFIX}/`) ||
    pathname.includes("personal-channel")
  );
}

/** Either surface. */
export function isSpotlightPath(pathname: string): boolean {
  return isSpotlightWizardPath(pathname) || isSpotlightViewPath(pathname);
}

/** Re-exported so tests can assert against the real values, not copies. */
export const SPOTLIGHT_PATHS = {
  wizard: PageRoutes.MyNAHLTV,
  view: PageRoutes.PersonalChannel,
  legacyWizard: LegacyPageRoutes.MyNAHLTV,
  legacyView: LegacyPageRoutes.PersonalChannel,
};
